import { useHeaderCollapse } from "@portal/hooks/useHeaderCollapse";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

export const BOND_TAB_IDS = ["overview", "bond-form", "periods-and-adjustments"] as const;

export type BondTabId = (typeof BOND_TAB_IDS)[number];

export const DEFAULT_BOND_TAB: BondTabId = "overview";

const isBondTabId = (value: string | null): value is BondTabId =>
	value !== null && BOND_TAB_IDS.some((id) => id === value);

export const useBondTabNavigation = ({ tabsEnabled }: { tabsEnabled: boolean }) => {
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const [searchParams] = useSearchParams();
	const { headerCollapsed, setHeaderCollapsed, getHeaderCollapsed } = useHeaderCollapse();

	const rawTabId = searchParams.get("t");
	const activeTabId = useMemo<BondTabId>(() => {
		if (!tabsEnabled) return DEFAULT_BOND_TAB;
		return isBondTabId(rawTabId) ? rawTabId : DEFAULT_BOND_TAB;
	}, [tabsEnabled, rawTabId]);

	const setActiveTabId = useCallback(
		(tabId: string) => {
			const next = new URLSearchParams(search);
			next.set("t", tabId);
			const query = next.toString();
			navigate({ pathname, search: query ? `?${query}` : "" }, { replace: true });
		},
		[navigate, pathname, search],
	);

	return {
		activeTabId,
		setActiveTabId,
		headerCollapsed,
		setHeaderCollapsed,
		getHeaderCollapsed,
	};
};
